import { useEffect, useState } from 'react';
import Modal from '../ui/Modal';

const emptyValues = {
  name: '',
  city: '',
  addressLine: '',
  contactPhone: '',
  contactEmail: '',
  googleMapsUrl: '',
  isActive: true,
};

const BranchForm = ({ isOpen, onClose, onSubmit, branch, submitting }) => {
  const [values, setValues] = useState(emptyValues);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (branch) {
      setValues({
        name: branch.name || '',
        city: branch.city || '',
        addressLine: branch.addressLine || '',
        contactPhone: branch.contactPhone || '',
        contactEmail: branch.contactEmail || '',
        googleMapsUrl: branch.googleMapsUrl || '',
        isActive: branch.isActive !== false,
      });
    } else {
      setValues(emptyValues);
    }
    setError('');
  }, [isOpen, branch]);

  const handleChange = (field) => (event) => {
    const value =
      event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!values.name.trim()) {
      setError('Branch name is required.');
      return;
    }
    try {
      await onSubmit(values);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save branch.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="bg-white">
        <div className="px-5 pt-5 pb-2">
          <h3 className="text-lg font-semibold text-stone-900">
            {branch ? 'Edit branch' : 'Add branch'}
          </h3>
          <p className="mt-1 text-sm text-stone-500">
            Branches are used when creating courses, assigning students, and tagging
            payments.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
          <label className="col-span-1 sm:col-span-2">
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Name <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              required
              value={values.name}
              onChange={handleChange('name')}
              placeholder="e.g. Colombo"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">City</span>
            <input
              type="text"
              value={values.city}
              onChange={handleChange('city')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">Phone</span>
            <input
              type="tel"
              value={values.contactPhone}
              onChange={handleChange('contactPhone')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label className="col-span-1 sm:col-span-2">
            <span className="mb-1 block text-sm font-medium text-stone-700">Address</span>
            <input
              type="text"
              value={values.addressLine}
              onChange={handleChange('addressLine')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">Email</span>
            <input
              type="email"
              value={values.contactEmail}
              onChange={handleChange('contactEmail')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Google Maps URL
            </span>
            <input
              type="url"
              value={values.googleMapsUrl}
              onChange={handleChange('googleMapsUrl')}
              placeholder="https://maps.google.com/..."
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label className="col-span-1 inline-flex items-center gap-2 sm:col-span-2">
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={handleChange('isActive')}
              className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="text-sm text-stone-700">
              Active (available when creating batches)
            </span>
          </label>
        </div>
        {error ? (
          <p className="px-5 pb-2 text-sm text-red-600">{error}</p>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-stone-200 bg-stone-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Saving...' : branch ? 'Save changes' : 'Create branch'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default BranchForm;
